import { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Check, TrendingUp, Users, Star } from "lucide-react";
import { supabase } from "@/lib/supabase/client";
import { usePortalLanguage } from "@/contexts/PortalLanguageContext";

// ── Brand tokens ──────────────────────────────────────────────────────────────
const NAVY  = "#1A1A2E";
const EMBER = "#FF6B35";
const TEAL  = "#00BFA5";

const SB_URL  = import.meta.env.VITE_SUPABASE_URL  as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// ── Field helpers ─────────────────────────────────────────────────────────────
const REFERRAL_OPTIONS = [
  "Google",
  "Social Media",
  "HPB Trade Show",
  "Manufacturer referral",
  "Friend / colleague",
  "Other",
] as const;

type ReferralOption = typeof REFERRAL_OPTIONS[number] | "";

interface FormState {
  firstName:       string;
  lastName:        string;
  storeName:       string;
  email:           string;
  password:        string;
  confirmPassword: string;
  country:         "" | "CA" | "US";
  city:            string;
  stateProvince:   string;
  referral:        ReferralOption;
  termsAgreed:     boolean;
}

type FieldErrors = Partial<Record<keyof FormState, string>>;

const BLANK: FormState = {
  firstName: "", lastName: "", storeName: "", email: "",
  password: "", confirmPassword: "", country: "", city: "", stateProvince: "",
  referral: "", termsAgreed: false,
};

// ── Input class helper ────────────────────────────────────────────────────────
const inputCls = (error?: string) =>
  [
    "w-full rounded-lg border px-3 py-2.5 text-sm bg-gray-900 text-white placeholder-gray-500",
    "focus:outline-none focus:ring-2 focus:border-transparent transition",
    error
      ? "border-red-500 focus:ring-red-500"
      : "border-gray-700 focus:ring-orange-500",
  ].join(" ");

// ── Main component ────────────────────────────────────────────────────────────
export default function SignUp() {
  const { t } = usePortalLanguage();
  // step: "account" | "confirm_email". There is no plan or price step: what a retailer pays is
  // agreed in their Order Form and invoiced by Fyrris (see create-retailer-invoice).
  const [step, setStep] = useState<"account" | "confirm_email">("account");

  const [form,    setForm]    = useState<FormState>(BLANK);
  const [errors,  setErrors]  = useState<FieldErrors>({});
  const [busy,    setBusy]    = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // ── Field update ────────────────────────────────────────────────────────────
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => { const n = { ...e }; delete n[k]; return n; });
    setServerError(null);
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  function validate(): FieldErrors {
    const e: FieldErrors = {};
    if (!form.firstName.trim())  e.firstName       = t("signup.errorRequired",  "Required");
    if (!form.lastName.trim())   e.lastName        = t("signup.errorRequired",  "Required");
    if (!form.storeName.trim())  e.storeName       = t("signup.errorRequired",  "Required");
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
                                 e.email           = t("signup.errorEmail",     "Valid email required");
    if (form.password.length < 6) e.password       = t("signup.errorPassword", "At least 6 characters");
    if (form.password !== form.confirmPassword)
                                 e.confirmPassword = t("signup.errorMatch",     "Passwords don't match");
    if (form.country !== "CA" && form.country !== "US")
                                 e.country         = t("signup.errorRequired",  "Required");
    if (!form.city.trim())       e.city            = t("signup.errorRequired",  "Required");
    if (!form.stateProvince.trim()) e.stateProvince = t("signup.errorRequired", "Required");
    if (!form.termsAgreed)       e.termsAgreed     = t("signup.errorTerms",     "You must agree to the terms");
    return e;
  }

  // ── Step 1 submit: create account ─────────────────────────────────────────
  const onSubmitAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    const fieldErrors = validate();
    if (Object.keys(fieldErrors).length) { setErrors(fieldErrors); return; }

    setBusy(true);
    setServerError(null);

    try {
      const { data, error } = await supabase.auth.signUp({
        email:    form.email,
        password: form.password,
        options: {
          data: {
            full_name:  `${form.firstName} ${form.lastName}`.trim(),
            first_name: form.firstName,
            last_name:  form.lastName,
          },
        },
      });

      if (error) throw error;

      if (data.user) {

        // Creates both the profile and retailer rows server-side, using the
        // service role — this project requires email confirmation
        // (mailer_autoconfirm=false), so signUp() never returns a session at
        // this point, and a client-side insert authenticated as anon gets
        // correctly rejected by retailers' owner-scoped RLS policies.
        const cRes = await fetch(`${SB_URL}/functions/v1/complete-signup`, {
          method:  "POST",
          headers: {
            apikey:         ANON_KEY,
            Authorization:  `Bearer ${ANON_KEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id:         data.user.id,
            email:           data.user.email,
            first_name:      form.firstName,
            last_name:       form.lastName,
            store_name:      form.storeName,
            country:         form.country,
            city:            form.city.trim()          || null,
            state:           form.stateProvince.trim() || null,
            referral_source: form.referral || null,
          }),
        });
        if (!cRes.ok) {
          const errBody = await cRes.json().catch(() => ({}));
          console.error("[SignUp] complete-signup failed:", cRes.status, errBody);
          throw new Error("Failed to create your retailer account. Please try again or contact support@fyrris.com.");
        }
        // Account, retailer, role, pairing code and country are all created server-side.
        await cRes.json().catch(() => ({}));
        setStep("confirm_email");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t("signup.errorServer", "Something went wrong.");
      setServerError(msg);
    } finally {
      setBusy(false);
    }
  };

  // ── Confirm email screen ─────────────────────────────────────────────────
  if (step === "confirm_email") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" style={{ backgroundColor: NAVY }}>
        <Helmet>
          <title>{t("signup.meta.title", "Create Your Retailer Account — Fyrris")}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: `${TEAL}22` }}>
          <Check className="w-7 h-7" style={{ color: TEAL }} />
        </div>
        <h1 className="text-2xl font-bold text-white mb-3">
          {t("signup.successTitle", "Check your email")}
        </h1>
        <p className="text-gray-400 max-w-sm leading-relaxed">
          {t("signup.successBody", "We sent a confirmation link to your email. Click it to confirm your account. Your Fyrris contact then sets up your agreement, and portal access is switched on once it is in place.")}
        </p>
        <Link
          to="/signin"
          className="mt-8 inline-flex items-center justify-center rounded-full px-8 py-3 text-sm font-medium text-white whitespace-nowrap transition hover:opacity-90"
          style={{ backgroundColor: EMBER }}
        >
          {t("signup.backToSignIn", "Go to Sign In")}
        </Link>
      </div>
    );
  }

  // ── Step 1: Account form ──────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#1A1A2E", display: "flex", alignItems: "center", justifyContent: "center", padding: "3rem 1rem" }}>
      <Helmet>
        <title>{t("signup.meta.title", "Create Your Retailer Account — Fyrris")}</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div style={{ width: "100%", maxWidth: "960px" }}>
        <div className="w-full max-w-5xl flex rounded-2xl shadow-2xl overflow-hidden">

          {/* ── Left brand panel (desktop only) ─────────────────────── */}
          <div
            className="hidden lg:flex lg:w-2/5 flex-shrink-0 flex-col justify-between p-10 xl:p-12"
            style={{ backgroundColor: NAVY }}
          >
            <img src="/fyrris-logo.png" alt="Fyrris" className="h-9 w-auto object-contain" />

            <div className="space-y-8">
              <div>
                <p className="text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: EMBER }}>
                  {t("signup.leftEyebrow", "For HPB Retailers")}
                </p>
                <h2 className="text-3xl xl:text-4xl font-bold text-white leading-snug">
                  {t("signup.leftHeadline", "Design Your Moments")}
                </h2>
                <p className="mt-3 text-gray-400 leading-relaxed">
                  {t("signup.leftSubhead", "Join the AR platform built for fireplace, BBQ, and patio specialty retailers.")}
                </p>
              </div>

              <ul className="space-y-4">
                {[
                  { icon: TrendingUp, text: t("signup.leftBullet1", "Turn AR browsers into showroom buyers"), color: EMBER },
                  { icon: Users,      text: t("signup.leftBullet2", "Reach customers before they visit a competitor"), color: TEAL },
                  { icon: Star,       text: t("signup.leftBullet3", "A direct line to the founder."), color: TEAL },
                ].map(({ icon: Icon, text, color }) => (
                  <li key={text} className="flex items-start gap-3">
                    <div className="mt-0.5 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}22` }}>
                      <Icon className="w-3.5 h-3.5" style={{ color }} />
                    </div>
                    <span className="text-sm text-gray-300 leading-relaxed">{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-xs text-gray-600">
              {t("signup.leftFooter", "Join a limited first cohort of retailers.")}
            </p>
          </div>

          {/* ── Right form panel ──────────────────────────────────────── */}
          <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-10 py-10 bg-gray-950">
            <div className="lg:hidden mb-8">
              <img src="/fyrris-logo.png" alt="Fyrris" className="h-8 w-auto object-contain mx-auto" />
            </div>

            <div className="w-full max-w-lg">
              <h1 className="text-2xl font-bold text-white mb-1">
                {t("signup.title", "Create Your Account")}
              </h1>
              <p className="text-gray-400 text-sm mb-8">
                {t("signup.subtitle", "Join Fyrris to access your retailer portal.")}
              </p>

              <form onSubmit={onSubmitAccount} noValidate className="space-y-5">

                {/* First + Last name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 mb-1.5" style={{ fontSize: "0.875rem", fontWeight: "500" }}>
                      {t("signup.firstName", "First name")} <span style={{ color: EMBER }}>*</span>
                    </label>
                    <input
                      type="text" autoComplete="given-name"
                      placeholder={t("signup.firstNamePlaceholder", "Jane")}
                      value={form.firstName} onChange={(e) => set("firstName", e.target.value)}
                      className={inputCls(errors.firstName)} style={{ fontSize: "1rem" }}
                    />
                    {errors.firstName && <FieldError msg={errors.firstName} />}
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-1.5" style={{ fontSize: "0.875rem", fontWeight: "500" }}>
                      {t("signup.lastName", "Last name")} <span style={{ color: EMBER }}>*</span>
                    </label>
                    <input
                      type="text" autoComplete="family-name"
                      placeholder={t("signup.lastNamePlaceholder", "Smith")}
                      value={form.lastName} onChange={(e) => set("lastName", e.target.value)}
                      className={inputCls(errors.lastName)} style={{ fontSize: "1rem" }}
                    />
                    {errors.lastName && <FieldError msg={errors.lastName} />}
                  </div>
                </div>

                {/* Store name */}
                <div>
                  <label className="block text-gray-400 mb-1.5" style={{ fontSize: "0.875rem", fontWeight: "500" }}>
                    {t("signup.storeName", "Business / Store name")} <span style={{ color: EMBER }}>*</span>
                  </label>
                  <input
                    type="text" autoComplete="organization"
                    placeholder={t("signup.storeNamePlaceholder", "e.g. Hearth & Home Retailers")}
                    value={form.storeName} onChange={(e) => set("storeName", e.target.value)}
                    className={inputCls(errors.storeName)} style={{ fontSize: "1rem" }}
                  />
                  {errors.storeName && <FieldError msg={errors.storeName} />}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-gray-400 mb-1.5" style={{ fontSize: "0.875rem", fontWeight: "500" }}>
                    {t("signup.email", "Email address")} <span style={{ color: EMBER }}>*</span>
                  </label>
                  <input
                    type="email" autoComplete="email"
                    placeholder={t("signup.emailPlaceholder", "jane@yourstore.com")}
                    value={form.email} onChange={(e) => set("email", e.target.value)}
                    className={inputCls(errors.email)} style={{ fontSize: "1rem" }}
                  />
                  {errors.email && <FieldError msg={errors.email} />}
                </div>

                {/* Password + Confirm */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 mb-1.5" style={{ fontSize: "0.875rem", fontWeight: "500" }}>
                      {t("signup.password", "Password")} <span style={{ color: EMBER }}>*</span>
                    </label>
                    <input
                      type="password" autoComplete="new-password" placeholder="••••••••"
                      value={form.password} onChange={(e) => set("password", e.target.value)}
                      className={inputCls(errors.password)} style={{ fontSize: "1rem" }}
                    />
                    {errors.password && <FieldError msg={errors.password} />}
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-1.5" style={{ fontSize: "0.875rem", fontWeight: "500" }}>
                      {t("signup.confirmPassword", "Confirm password")} <span style={{ color: EMBER }}>*</span>
                    </label>
                    <input
                      type="password" autoComplete="new-password" placeholder="••••••••"
                      value={form.confirmPassword} onChange={(e) => set("confirmPassword", e.target.value)}
                      className={inputCls(errors.confirmPassword)} style={{ fontSize: "1rem" }}
                    />
                    {errors.confirmPassword && <FieldError msg={errors.confirmPassword} />}
                  </div>
                </div>

                {/* Country — explicit choice, no default: the retailers table defaults to 'US',
                    which silently mislabels Canadian stores. */}
                <div>
                  <label className="block text-gray-400 mb-1.5" style={{ fontSize: "0.875rem", fontWeight: "500" }}>
                    {t("signup.country", "Country")} <span style={{ color: EMBER }}>*</span>
                  </label>
                  <select
                    value={form.country}
                    onChange={(e) => set("country", e.target.value as FormState["country"])}
                    autoComplete="country"
                    className={[inputCls(errors.country), "appearance-none cursor-pointer", form.country === "" ? "text-gray-500" : "text-white"].join(" ")}
                    style={{ fontSize: "1rem" }}
                  >
                    <option value="" disabled>{t("signup.countryPlaceholder", "Select country…")}</option>
                    <option value="CA" className="bg-gray-900 text-white">{t("signup.countryCA", "Canada")}</option>
                    <option value="US" className="bg-gray-900 text-white">{t("signup.countryUS", "United States")}</option>
                  </select>
                  {errors.country && <FieldError msg={errors.country} />}
                </div>

                {/* City + State */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 mb-1.5" style={{ fontSize: "0.875rem", fontWeight: "500" }}>
                      {t("signup.city", "City")} <span style={{ color: EMBER }}>*</span>
                    </label>
                    <input
                      type="text" autoComplete="address-level2"
                      placeholder={t("signup.cityPlaceholder", "e.g. Toronto")}
                      value={form.city} onChange={(e) => set("city", e.target.value)}
                      className={inputCls(errors.city)} style={{ fontSize: "1rem" }}
                    />
                    {errors.city && <FieldError msg={errors.city} />}
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-1.5" style={{ fontSize: "0.875rem", fontWeight: "500" }}>
                      {t("signup.stateProvince", "State / Province")} <span style={{ color: EMBER }}>*</span>
                    </label>
                    <input
                      type="text" autoComplete="address-level1"
                      placeholder={form.country === "US"
                        ? t("signup.stateProvincePlaceholderUS", "e.g. TX")
                        : t("signup.stateProvincePlaceholder", "e.g. ON")}
                      value={form.stateProvince} onChange={(e) => set("stateProvince", e.target.value)}
                      className={inputCls(errors.stateProvince)} style={{ fontSize: "1rem" }}
                    />
                    {errors.stateProvince && <FieldError msg={errors.stateProvince} />}
                  </div>
                </div>

                {/* Referral */}
                <div>
                  <label className="block text-gray-400 mb-1.5" style={{ fontSize: "0.875rem", fontWeight: "500" }}>
                    {t("signup.referral", "How did you hear about us?")}
                  </label>
                  <select
                    value={form.referral}
                    onChange={(e) => set("referral", e.target.value as ReferralOption)}
                    className={[inputCls(), "appearance-none cursor-pointer", form.referral === "" ? "text-gray-500" : "text-white"].join(" ")}
                    style={{ fontSize: "1rem" }}
                  >
                    <option value="" disabled>{t("signup.referralPlaceholder", "Select one…")}</option>
                    {REFERRAL_OPTIONS.map((opt) => (
                      <option key={opt} value={opt} className="bg-gray-900 text-white">
                        {t(`signup.referral_${opt.toLowerCase().replace(/[^a-z]/g, "_")}`, opt)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Terms */}
                <div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox" checked={form.termsAgreed}
                      onChange={(e) => set("termsAgreed", e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-600 accent-orange-500 cursor-pointer flex-shrink-0"
                    />
                    <span className="text-sm text-gray-400 leading-relaxed">
                      {t("signup.termsPrefix", "I agree to the")}{" "}
                      <Link to="/terms" target="_blank" rel="noopener noreferrer"
                        className="underline underline-offset-2 transition-opacity hover:opacity-75" style={{ color: EMBER }}>
                        {t("signup.termsLink", "Terms and Conditions")}
                      </Link>
                    </span>
                  </label>
                  {errors.termsAgreed && <FieldError msg={errors.termsAgreed} />}
                </div>

                {/* Server error */}
                {serverError && (
                  <div className="rounded-lg border border-red-900/40 bg-red-900/20 px-4 py-3 text-sm text-red-400">
                    {serverError}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit" disabled={busy}
                  className="w-full inline-flex items-center justify-center rounded-full py-3 text-sm font-semibold text-white whitespace-nowrap transition hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: EMBER, boxShadow: "0 4px 14px rgba(255,107,53,0.25)", fontSize: "1rem" }}
                >
                  {busy
                    ? t("signup.submitting", "Creating account…")
                    : t("signup.submit", "Continue to Plan Selection →")}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-gray-500">
                {t("signup.alreadyHaveAccount", "Already have an account?")}{" "}
                <Link to="/signin" className="font-medium underline underline-offset-2 transition-opacity hover:opacity-75" style={{ color: EMBER }}>
                  {t("signup.signIn", "Sign in")}
                </Link>
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Small helper ──────────────────────────────────────────────────────────────
function FieldError({ msg }: { msg: string }) {
  return <p className="mt-1 text-xs text-red-400">{msg}</p>;
}
